import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DayBlock({ id, rows, noteText, open, onToggle, t, label }) {
  return (
    <div className="border border-[#27272A] bg-[#121214]" data-testid={`prog-day-${id}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`prog-day-toggle-${id}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-[#0A0A0C] transition-colors"
      >
        <span className="font-display uppercase font-medium text-sm sm:text-base">{label}</span>
        <span className="flex items-center gap-3 shrink-0">
          <span className="text-[10px] uppercase tracking-wider text-[#71717A]">{rows.length} {t("prog.match").toLowerCase()}{rows.length > 1 ? "s" : ""}</span>
          <ChevronDown size={16} className={`text-[#C5A059] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="px-6 pb-6 overflow-x-auto">
          <DayTable rows={rows} testid={`prog-day-table-${id}`} />
          {noteText && <p className="mt-4 text-xs text-[#C5A059] font-editorial italic leading-relaxed">{noteText}</p>}
        </div>
      )}
    </div>
  );
}

/** Table d'un jour du programme officiel : Heure | Tournoi | Match | Format | Score */
const TOURNOI_BADGES = {
  masc: ["Masculin", "border-sky-400/50 text-sky-300"],
  fem: ["Féminin", "border-pink-400/50 text-pink-300"],
  etoiles: ["Étoiles", "border-[#C5A059]/50 text-[#C5A059]"],
  act: ["Animations", "border-[#3f3f46] text-[#71717A]"],
};

function DayTable({ rows, testid }) {
  return (
    <table className="w-full text-sm min-w-[720px]" data-testid={testid}>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-[#27272A]/60" data-testid={r.testid}>
            <td className="py-2.5 pr-4 text-[#C5A059] font-medium whitespace-nowrap">{r.heure}</td>
            <td className="py-2.5 pr-4 whitespace-nowrap">
              <span className={`inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${TOURNOI_BADGES[r.tournoi]?.[1] || "border-[#27272A] text-[#71717A]"}`}>
                {TOURNOI_BADGES[r.tournoi]?.[0] || r.tournoi}
              </span>
            </td>
            {r.activite ? (
              <td colSpan={2} className="py-2.5 pr-4 text-[#C5A059] font-editorial italic">{r.match}</td>
            ) : (
              <>
                <td className="py-2.5 pr-4 text-[#A1A1AA]">{r.match}</td>
                <td className="py-2.5 pr-4 text-[#71717A] text-xs whitespace-nowrap">{r.format || ""}</td>
              </>
            )}
            <td className={`py-2.5 text-right font-display font-bold ${r.score ? "text-white" : "text-[#3f3f46]"}`}>{r.score || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const JOURS2_LABELS = {
  "2026-11-17": "Mardi 17 Novembre — Tournoi masculin : huitièmes & quarts",
  "2026-11-18": "Mercredi 18 Novembre — Tournoi féminin (classement 1re à 8e place)",
  "2026-11-19": "Jeudi 19 Novembre — Finales masculines · Détection des étoiles",
  "2026-11-21": "Samedi 21 Novembre — Tournoi des Étoiles · Remise des trophées",
};

const JOURS2_NOTES = {
  "2026-11-18": "Classement final : 1re place = vainqueur de la finale · 2e = finaliste · 3e = vainqueur de la petite finale · 5e/6e et 7e/8e départagés par les matchs de classement. Remise des trophées de 14h50 à 15h00.",
  "2026-11-19": "Champion du tournoi masculin sacré à 10h50. L'après-midi est consacré à la détection des étoiles : sélection des meilleurs joueurs puis annonce des 8 équipes All-Star.",
  "2026-11-21": "Finale du Tournoi des Étoiles en 30 minutes à 12h30. Photos et distinctions de 13h00 à 14h00, remise officielle des trophées de 14h00 à 14h30.",
};

const PROG2_RECAP = [
  ["Tournoi masculin — 16 équipes", "Équipes mixtes U19/U23 · matchs de 20 min · élimination directe · grande finale le 19 nov à 10h30"],
  ["Tournoi féminin — 8 équipes", "Le 18 novembre de 09h00 à 15h00 · classement complet de la 1re à la 8e place · finale à 14h30"],
  ["Tournoi des Étoiles — 8 sélections", "Les meilleurs joueurs détectés le 19 nov · All-Star A à H le 21 nov · finale de 30 min à 12h30"],
  ["Clôture", "Photos et distinctions 13h00-14h00 · remise officielle des trophées le 21 nov de 14h00 à 14h30"],
  ["Bilan", "36 matchs de tournoi (16 masculins + 12 féminins + 8 All-Star) + cérémonies sur 4 jours"],
  ["Format des matchs", "20 minutes + 10 minutes entre matchs · finale des Étoiles en 30 minutes"],
];

export default function ProgrammeOfficiel() {
  const { t } = useLang();
  const [open, setOpen] = useState({});
  const [visible, setVisible] = useState(true);
  const [matchs, setMatchs] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => {
      setVisible(r.data.programme_visible !== false);
    }).catch(() => {});
    // Le Programme 2 est le seul programme officiel
    axios.get(`${API}/matchs`, { params: { programme: 2 } }).then((r) => setMatchs(r.data)).catch(() => setMatchs([]));
  }, []);

  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  return (
    <section data-testid="programme-officiel" className="py-24 lg:py-32 bg-[#0A0A0C] relative">
      <div className={`max-w-7xl mx-auto px-6 lg:px-10 transition-all duration-700 ${visible ? "" : "blur-md select-none pointer-events-none"}`}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("prog.eyebrow")}</p>
          <h2 className="font-display text-4xl md:text-5xl uppercase font-bold tracking-tight leading-tight">
            {t("prog.title.a")} <span className="text-[#C5A059]">{t("prog.title.b")}</span>
          </h2>
          <p className="text-lg leading-relaxed text-[#A1A1AA] mt-6 max-w-3xl">{t("prog.intro")}</p>
        </motion.div>

        <div data-testid="programme-2">
          {/* Récapitulatif du format */}
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="prog2-recap">
            {PROG2_RECAP.map(([titre, detail]) => (
              <div key={titre} className="border border-[#27272A] bg-[#121214] p-5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{titre}</p>
                <p className="text-xs text-[#A1A1AA] mt-2 leading-relaxed">{detail}</p>
              </div>
            ))}
          </div>

          {/* Jours — chaque tour est dévoilé à l'issue du précédent */}
          <p className="mt-8 text-xs text-[#71717A] italic font-editorial" data-testid="prog2-note-devoilement">
            Le calendrier de chaque tour (quarts, demi-finales, finales) se dévoile automatiquement à l'issue du tour précédent.
          </p>
          {matchs === null ? (
            <p className="mt-10 text-[#71717A] text-sm">Chargement…</p>
          ) : (
            <div className="space-y-3 mt-6">
              {Object.keys(JOURS2_LABELS).map((jour) => {
                const rows = matchs.filter((m) => m.jour === jour && m.visible !== false).map((m) => ({
                  heure: m.heure,
                  match: m.phase === "activite" ? m.label : `${m.label} · ${m.equipe_a} vs ${m.equipe_b}`,
                  score: m.statut === "joue" && m.score_a != null ? `${m.score_a} - ${m.score_b}` : null,
                  tournoi: m.tournoi,
                  format: m.format,
                  activite: m.phase === "activite",
                  testid: `prog2-match-${m.ordre}`,
                }));
                if (!rows.length) return null;
                return (
                  <DayBlock key={jour} id={jour} label={JOURS2_LABELS[jour]} rows={rows} noteText={JOURS2_NOTES[jour]}
                    open={!!open[jour]} onToggle={() => toggle(jour)} t={t} />
                );
              })}
            </div>
          )}
        </div>
      </div>
      {!visible && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <span className="bg-[#0A0A0C]/90 border border-[#C5A059] text-[#C5A059] text-xs uppercase tracking-[0.4em] px-6 py-3" data-testid="programme-coming-soon">
            Coming Soon
          </span>
        </div>
      )}
    </section>
  );
}
